import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  List,
  ListItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import CustomTextInput from 'components/Forms/CustomTextInput';
import { useProviderSettings } from 'hooks/useProviderSettings';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';
import { ROUNDUP_TYPES, ROUNDUP_THRESHOLD } from 'constants/serviceFee';

const ProviderServicingFee = ({ type, methods }) => {
  const { t } = useTranslation();
  const {
    providers,
    data,
    isDisableServiceFee,
    serviceFeeFormMethods,
    showFee,
    convertNameByProvider,
    register,
    handleOpenModal,
  } = useProviderSettings(methods, type);
  const { getValues } = serviceFeeFormMethods;
  const checkPermission = usePermission();
  const roundupChecked = serviceFeeFormMethods.watch('roundup');

  return (
    <>
      <FormGroup
        sx={{ width: '180px', padding: '16px' }}
        onClick={() => handleOpenModal}
      >
        <Controller
          name="enabled"
          control={serviceFeeFormMethods.control}
          render={({ field }) => (
            <FormControlLabel
              disabled={!checkPermission(all['SUPER'])}
              sx={{
                width: 'max-content',
                // '& .MuiFormControlLabel-label.Mui-disabled': {
                //   color: '#93c2fd',
                //   opacity: 0.6,
                // },
                // '& .MuiSwitch-switchBase.Mui-disabled': {
                //   color: '#307ff9',
                // },
                // '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                //   backgroundColor: '#307ff9',
                //   opacity: 0.6,
                // },
              }}
              control={
                <Switch
                  disabled={!checkPermission(all['SUPER'])}
                  checked={field.value || false}
                  onChange={field.onChange}
                  name={field.name}
                />
              }
              label={t('providers_settings.enable_service_fee')}
            />
          )}
        />
      </FormGroup>
      {showFee && (
        <Box sx={{ width: '295px', paddingLeft: '16px' }}>
          <CustomTextInput
            inputProps={{ type: 'number', min: '0' }}
            min="0"
            name="percent"
            label="Service fee(%)"
            size="small"
            sx={{ width: '100%', mt: 1 }}
            disabled={!checkPermission(all['SUPER'])}
          />
          <Typography sx={{ fontSize: '12px', mt: 1 }}>
            {t('providers_settings.once_applied_added')}
            {` ${convertNameByProvider(data?.name)} `}{' '}
            {t('providers_settings.menu_items_and')}
            {` `} {`${convertNameByProvider(data?.name)}`}
            {` `}
            {t('providers_settings.price_')}
          </Typography>
          <Box
            sx={{
              width: '541px',
            }}
          >
            <FormGroup sx={{ mt: 2 }}>
              <Controller
                name="roundup"
                control={serviceFeeFormMethods.control}
                render={({ field }) => (
                  <FormControlLabel
                    disabled={!checkPermission(all['SUPER'])}
                    control={
                      <Checkbox
                        checked={field?.value || false}
                        onChange={field?.onChange}
                        name={field?.name}
                      />
                    }
                    label={t('providers_settings.round_up_provider_prices')}
                  />
                )}
              />
            </FormGroup>
            {roundupChecked && (
              <>
              <FormControl
                sx={{ px: 2, mt: 1 }}
                disabled={!checkPermission(all['SUPER'])}
              >
                <FormLabel sx={{ fontSize: '12px' }}>
                  {t('providers_settings.round_up_type_label')}
                </FormLabel>
                <Controller
                  name="roundupType"
                  control={serviceFeeFormMethods.control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value || ROUNDUP_TYPES.TIER_49_99}
                    >
                      <FormControlLabel
                        value={ROUNDUP_TYPES.NEAREST_9}
                        disabled={!checkPermission(all['SUPER'])}
                        control={<Radio size="small" />}
                        label={
                          <Typography sx={{ fontSize: '12px' }}>
                            {t('providers_settings.round_up_type_nearest_9')}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value={ROUNDUP_TYPES.TIER_49_99}
                        disabled={!checkPermission(all['SUPER'])}
                        control={<Radio size="small" />}
                        label={
                          <Typography sx={{ fontSize: '12px' }}>
                            {t('providers_settings.round_up_type_tier')}
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value={ROUNDUP_TYPES.ALWAYS_99}
                        disabled={!checkPermission(all['SUPER'])}
                        control={<Radio size="small" />}
                        label={
                          <Typography sx={{ fontSize: '12px' }}>
                            {t('providers_settings.round_up_type_always_99')}
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
              <Box sx={{ px: 2, mt: 2 }}>
                <Controller
                  name="roundupThreshold"
                  control={serviceFeeFormMethods.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      size="small"
                      label={t('providers_settings.round_up_threshold_label')}
                      value={field.value ?? ROUNDUP_THRESHOLD}
                      disabled={!checkPermission(all['SUPER'])}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error ? t(fieldState.error.message) : ''
                      }
                      inputProps={{ min: 0, step: 'any' }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: '100%' }}
                    />
                  )}
                />
              </Box>
              </>
            )}
            <List
              sx={{
                px: 2,
                listStyle: 'inherit',
                '& li': {
                  display: 'list-item',
                  paddingLeft: 0,
                  paddingRight: 0,
                },
              }}
            >
              <ListItem>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('providers_settings.the_round_up_follows')}
                </Typography>
              </ListItem>
              <ListItem>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('providers_settings.e_g_if')}
                  {` `}
                  {`${convertNameByProvider(providers[data?.srv]?.label)}`}
                  {` `}
                  {t('providers_settings.menu_item_w_')}
                </Typography>
              </ListItem>
              <ListItem>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('providers_settings.e_g_the_price_to_')}
                </Typography>
              </ListItem>
              <ListItem>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('providers_settings.if')} {` `}
                  {` ${convertNameByProvider(data?.srv)}`}
                  {` `}
                  {t('providers_settings.menu_item_modifier_')}
                </Typography>
              </ListItem>
              <ListItem>
                <Typography sx={{ fontSize: '12px' }}>
                  {t('providers_settings.e_g_the_price_')} {` `}
                  {t('providers_settings.or')} {` `}
                  {t('providers_settings.e_g_the_price_after')}
                </Typography>
              </ListItem>
            </List>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ProviderServicingFee;
